import { useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { formatINR } from "../context/CartContext";
import { trackEvent } from "../lib/tracking";

function paymentLabel(method) {
  if (method === "cod") return "Cash on Delivery";
  if (method === "cod_partial") return "Cash on Delivery (partial online payment)";
  return "Razorpay (Prepaid)";
}

/**
 * A dedicated, stable URL (/thank-you) shoppers land on right after a
 * successful order — separate from /checkout so it can be set as the
 * Purchase event's landing page in Meta Events Manager (URL-based event
 * setup, custom conversions, exclusion audiences, etc). The actual
 * Purchase pixel/CAPI event fires from here (once, on mount), not from
 * Checkout.jsx, so every successful order — COD included, which
 * previously never fired one at all — lands on a real, trackable page
 * load instead of just a client-side state swap on /checkout.
 */
export default function ThankYou() {
  const location = useLocation();
  const navigate = useNavigate();
  const order = location.state?.order || null;
  const tracked = useRef(false);

  useEffect(() => {
    if (!order) {
      navigate("/", { replace: true });
      return;
    }
    window.scrollTo({ top: 0, behavior: "instant" });
    if (tracked.current) return;
    tracked.current = true;
    trackEvent("Purchase", {
      value: order.value,
      currency: "INR",
      content_ids: order.contentIds,
    }, { em: order.email, ph: order.phone });
    // eslint-disable-next-line
  }, []);

  if (!order) return null;

  const totals = order.totals || {};

  return (
    <div data-testid="thank-you-page" className="max-w-2xl mx-auto px-6 md:px-12 py-24">
      <div className="text-center">
        <div className="h-20 w-20 rounded-full bg-blush-100 grid place-items-center text-blush-600 mx-auto">
          <CheckCircle2 size={28} strokeWidth={1.4} />
        </div>
        <span className="mt-6 block font-script text-4xl text-blush-600">thank you</span>
        <h1 className="font-heading text-4xl md:text-5xl text-ink-900 mt-2">
          Your order is confirmed
        </h1>
        <p className="mt-4 font-body text-ink-700">
          Order <strong>{order.orderNumber}</strong> has been received. We'll email you the
          moment your pieces leave our atelier.
        </p>
        {order.codPartialDue > 0 && (
          <p className="mt-2 font-body text-ink-700">
            {formatINR(order.codPartialDue)} due in cash when your order is delivered.
          </p>
        )}
      </div>

      {order.items?.length > 0 && (
        <div className="mt-10 rounded-3xl bg-white border border-blush-100 p-6 md:p-8 shadow-sm text-left">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-2xl text-ink-900">Your order</h3>
            <span className="text-xs font-body uppercase tracking-[0.18em] text-blush-600" data-testid="thank-you-payment-method">
              {paymentLabel(order.paymentMethod)}
            </span>
          </div>
          <ul className="mt-5 space-y-4 max-h-[320px] overflow-y-auto pr-1">
            {order.items.map((i, idx) => (
              <li key={idx} className="flex gap-3">
                <div className="relative h-16 w-14 rounded-xl overflow-hidden">
                  {i.image && <img src={i.image} alt="" className="h-full w-full object-cover" />}
                  <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-blush-600 text-white text-[10px] grid place-items-center">
                    {i.quantity}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-body text-sm text-ink-900 truncate">{i.name}</p>
                  <p className="font-body text-xs text-ink-500">Size {i.size}</p>
                </div>
                <p className="font-body text-sm text-ink-900">{formatINR(i.price * i.quantity)}</p>
              </li>
            ))}
          </ul>

          <dl className="mt-6 space-y-2 font-body text-ink-700 text-sm">
            <div className="flex items-center justify-between"><dt>Subtotal</dt><dd>{formatINR(totals.subtotal)}</dd></div>
            {totals.discount > 0 && (
              <div className="flex items-center justify-between text-blush-600"><dt>Discount{totals.couponCode ? ` (${totals.couponCode})` : ""}</dt><dd>- {formatINR(totals.discount)}</dd></div>
            )}
            <div className="flex items-center justify-between"><dt>Shipping</dt><dd>{totals.shipping === 0 ? "Free" : formatINR(totals.shipping)}</dd></div>
            {totals.tax > 0 && (
              <div className="flex items-center justify-between"><dt>Tax</dt><dd>{formatINR(totals.tax)}</dd></div>
            )}
            <div className="h-px bg-blush-100 my-2" />
            <div className="flex items-center justify-between text-lg">
              <dt className="font-heading text-ink-900">Total</dt>
              <dd className="font-heading text-ink-900">{formatINR(totals.total)}</dd>
            </div>
          </dl>
        </div>
      )}

      <div className="mt-10 flex items-center justify-center gap-3">
        <Link to="/shop" className="btn-maroon">Keep exploring</Link>
        <button onClick={() => navigate("/")} className="btn-ghost-rose">Back to home</button>
      </div>
    </div>
  );
}
